import { chartColour } from './palette';

export interface BarDatum {
  /** Eindeutig innerhalb des Diagramms. */
  key: string;
  /** Was an der Achse steht. */
  label: string;
  /** Der Wert, nach dem die Laenge sich richtet. */
  value: number;
  /** Der Wert, wie er dasteht -- gerundet, mit Einheit. */
  display: string;
  /** Erzwingt einen Farbton statt der Reihenfolge. Fuer Zustaende. */
  colour?: string;
}

interface BarChartProps {
  data: readonly BarDatum[];
  /** Beschreibt das Diagramm fuer vorlesende Programme. */
  caption: string;
  /**
   * Der Wert, der volle Laenge bedeutet. Ohne Angabe der groesste Wert.
   *
   * Ausdruecklich setzbar, damit zwei Diagramme nebeneinander vergleichbar
   * bleiben: Zwei Balken gleicher Laenge, die verschiedene Zahlen meinen,
   * sind schlimmer als gar kein Diagramm.
   */
  max?: number;
}

/**
 * Waagerechte Balken.
 *
 * **Waagerecht und nicht senkrecht**, weil die Beschriftungen hier Namen sind
 * -- Warteschlangen, Kunden, Bearbeiter. Senkrechte Balken zwingen solche
 * Beschriftungen in eine Schraeglage oder in eine Abkuerzung, und beides liest
 * niemand.
 *
 * Gezeichnet mit ``div`` und Prozentbreiten und nicht mit SVG: Die
 * Beschriftung bleibt damit auswaehlbar, waechst mit der Schriftgroesse des
 * Browsers und braucht keine eigene Umbruchrechnung.
 *
 * **Die Zahl steht am Balken.** Ein Diagramm, dessen Werte man abschaetzen
 * muss, ist eine Dekoration. Wer den genauen Wert braucht -- und in einer
 * Auswertung braucht ihn jeder -- soll ihn nicht in der Tabelle darunter
 * suchen muessen.
 */
export function BarChart({ data, caption, max }: BarChartProps) {
  const ceiling = max ?? Math.max(...data.map((row) => row.value), 0);

  return (
    <ul className="chart-bars" aria-label={caption}>
      {data.map((row, index) => (
        <li key={row.key} className="chart-bars__row">
          <span className="chart-bars__label" title={row.label}>
            {row.label}
          </span>
          <span className="chart-bars__track">
            <span
              className="chart-bars__fill"
              style={{
                // Null bleibt sichtbar: Ein Balken der Breite null sieht aus
                // wie eine fehlende Zeile, und eine Warteschlange ohne
                // Vorgaenge ist eine Aussage und kein Loch.
                width: ceiling > 0 ? `${String(Math.max(1, (row.value / ceiling) * 100))}%` : '1%',
                background: row.colour ?? chartColour(index),
              }}
            />
          </span>
          <span className="chart-bars__value">{row.display}</span>
        </li>
      ))}
    </ul>
  );
}
